import { useEffect, useId, useMemo, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import { sameCalendar } from '../../core/calendar';
import { combinedInitialMonth, combinedMonthGrid, combinedMonths, initialMonth, monthGrid, schoolMonths, type DayTeaching, type MonthDay, type MonthSource, type MonthUnit, type MonthWeek, type YearMonth } from '../../core/month';
import type { ScheduledUnit } from '../../core/schedule';
import type { Calendar, IsoDate } from '../../core/types';
import { linkTo, planHref } from '../navigation';
import { unitColor } from '../unit-color';
import { day } from './UnitRow';

const WEEKDAYS = [['Mon', 'Monday'], ['Tue', 'Tuesday'], ['Wed', 'Wednesday'], ['Thu', 'Thursday'], ['Fri', 'Friday']] as const;
const monthName = ({ year, month }: YearMonth) => new Date(Date.UTC(year, month - 1, 1)).toLocaleDateString('en-US', { month: 'long', year: 'numeric', timeZone: 'UTC' });
const sameMonth = (a: YearMonth, b: YearMonth) => a.year === b.year && a.month === b.month;

/**
 * The plan as a month calendar (the left column of the editor and the weekly view). Each week
 * shows its unit as a named bar; breaks are greyed and named, today is outlined. Dates come from
 * the shared `schedule` and the server's `today` (KTD5, KTD8). My week's `CombinedCalendar`
 * draws every plan's days with the same table and day pieces.
 */
export function PlanCalendar({ rows, calendar, today, selectedUnitId, onSelectUnit }: {
  rows: ScheduledUnit[]; calendar: Calendar; today: IsoDate; selectedUnitId: string | null; onSelectUnit(unitId: string): void;
}) {
  const nav = useShownMonth(schoolMonths(calendar, rows), () => initialMonth(calendar, today, rows));
  const weeks = monthGrid(calendar, rows, nav.month.year, nav.month.month, today);
  return <section className="plan-calendar" aria-label="Plan calendar">
    <MonthTable nav={nav}>
      {weeks.map(week => <CalendarWeek key={week.monday} week={week} selectedUnitId={selectedUnitId} onSelect={u => onSelectUnit(u.id)} />)}
    </MonthTable>
  </section>;
}

/** One plan on the combined calendar, with its full course label for the differing-calendars notice (KTD6). */
export interface CombinedSource extends MonthSource { name: string }

/** The one plan picked when it is still a source, else every plan. */
const visible = <S extends { planId: string }>(sources: S[], planId: string | null): S[] => {
  const picked = planId ? sources.filter(s => s.planId === planId) : [];
  return picked.length ? picked : sources;
};

/**
 * Every plan's teaching on one month calendar (R9, R10): each day a dot per subject teaching it, in
 * its colour (KTD6), and named in the day's text, never by colour alone. The filter shows all
 * subjects or one; one subject is that plan alone, so its days follow its calendar. With several
 * shown, a notice says when their calendars differ and links to the first one. The legend below
 * names each colour and scrolls to that plan's section.
 */
export function CombinedCalendar({ sources, today, onSelectPlan }: { sources: CombinedSource[]; today: IsoDate; onSelectPlan(planId: string): void }) {
  const filterName = useId();
  const [only, setOnly] = useState<string | null>(null);
  const shown = useMemo(() => visible(sources, only), [sources, only]);
  const nav = useShownMonth(combinedMonths(shown), () => combinedInitialMonth(sources, today));
  const choose = (planId: string | null) => {
    const next = visible(sources, planId);
    // Stay on the shown month when the chosen plans still cover it.
    if (!combinedMonths(next).some(m => sameMonth(m, nav.month))) nav.show(combinedInitialMonth(next, today));
    setOnly(planId);
  };
  // The first plan shown, when the shown plans' calendars differ: the notice links to its calendar.
  const differ = useMemo(() => {
    const [first, ...rest] = shown;
    return first && rest.some(s => !sameCalendar(s.calendar, first.calendar)) ? first : null;
  }, [shown]);
  const weeks = combinedMonthGrid(shown, nav.month.year, nav.month.month, today);
  return <section className="plan-calendar combined-calendar" aria-label="Calendar for every plan">
    <fieldset className="cal-filter">
      <legend>Show subjects</legend>
      <label className={only === null ? 'selected' : undefined}><input type="radio" name={filterName} checked={only === null} onChange={() => choose(null)} />All subjects</label>
      {sources.map(s => <label key={s.planId} className={only === s.planId ? 'selected' : undefined}>
        <input type="radio" name={filterName} checked={only === s.planId} onChange={() => choose(s.planId)} />
        <span className="cal-swatch" aria-hidden="true" style={{ '--unit-color': unitColor(s.colorIndex) } as CSSProperties} />{s.label}
      </label>)}
    </fieldset>
    {differ && <p className="cal-notice">Your plans use different calendars. A day is greyed only when no plan teaches. <a {...linkTo(planHref(differ.planId))}>See the {differ.name} calendar</a></p>}
    <MonthTable nav={nav}>
      {weeks.map(week => <tbody key={week.monday} className="cal-week">
        <tr>{week.days.map(d => <DayCell key={d.date} d={d} teaching={d.teaching ?? []} />)}</tr>
      </tbody>)}
    </MonthTable>
    <ul className="cal-legend" aria-label="Subjects">
      {shown.map(s => <li key={s.planId}>
        <button type="button" onClick={() => onSelectPlan(s.planId)}>
          <span className="cal-swatch" aria-hidden="true" style={{ '--unit-color': unitColor(s.colorIndex) } as CSSProperties} />{s.label}
        </button>
      </li>)}
    </ul>
  </section>;
}

/** The month shown, kept inside `months` when an edit or the filter changes them. */
function useShownMonth(months: YearMonth[], initial: () => YearMonth) {
  const [shown, setShown] = useState<YearMonth>(initial);
  const found = months.findIndex(m => sameMonth(m, shown));
  const index = found >= 0 ? found : Math.max(0, months.length - 1);
  const month = months[index] ?? shown;
  return { month, index, count: months.length, show: setShown, go: (step: number) => { const target = months[index + step]; if (target) setShown(target); } };
}

/** The month heading, Previous and Next, and the Monday-to-Friday table around its weeks. */
function MonthTable({ nav, children }: { nav: ReturnType<typeof useShownMonth>; children: ReactNode }) {
  const headingId = useId();
  return <>
    <div className="cal-header">
      <button type="button" className="icon-button" aria-label="Previous month" disabled={nav.index <= 0} onClick={() => nav.go(-1)}>‹</button>
      <h2 id={headingId} aria-live="polite">{monthName(nav.month)}</h2>
      <button type="button" className="icon-button" aria-label="Next month" disabled={nav.index >= nav.count - 1} onClick={() => nav.go(1)}>›</button>
    </div>
    <table className="cal-grid" aria-labelledby={headingId}>
      <thead><tr>{WEEKDAYS.map(([short, full]) => <th key={short} scope="col"><abbr title={full}>{short}</abbr></th>)}</tr></thead>
      {children}
    </table>
  </>;
}

/** Who teaches on a combined-calendar day, for its text and title: `Math 4: Fractions; Science 4: Energy`. */
const teachingText = (teaching: DayTeaching[]) =>
  teaching.map(t => `${t.label}: ${t.unitName}${t.done ? ' (done)' : ''}${t.pastLastDay ? ' (past the last day)' : ''}`).join('; ');

/** A day: its number, its kind, and on the combined calendar a dot per plan teaching it, named in its text and title. */
function DayCell({ d, teaching }: { d: MonthDay; teaching?: DayTeaching[] }) {
  const classes = ['cal-day', `cal-${d.kind === 'outside_year' ? 'outside' : d.kind}`, d.inMonth ? '' : 'cal-other-month', d.isToday ? 'cal-today' : ''].filter(Boolean).join(' ');
  const text = `${day(d.date)}${d.breakName ? `, ${d.breakName}` : ''}${d.isToday ? ', today' : ''}`;
  if (!teaching) return <td className={classes} title={d.breakName} aria-current={d.isToday ? 'date' : undefined}>
    <span aria-hidden="true">{d.dayOfMonth}</span>
    <span className="sr-only">{text}</span>
  </td>;
  const full = teaching.length ? `${text}, ${teachingText(teaching)}` : text;
  return <td className={classes} title={full} aria-current={d.isToday ? 'date' : undefined}>
    <span aria-hidden="true">{d.dayOfMonth}</span>
    {teaching.length > 0 && <span className="cal-dots" aria-hidden="true">
      {teaching.map(t => <span key={`${t.planId}/${t.unitId}`} className={`cal-dot${t.done ? ' done' : ''}${t.pastLastDay ? ' over' : ''}`}
        style={{ '--unit-color': unitColor(t.colorIndex) } as CSSProperties} />)}
    </span>}
    <span className="sr-only">{full}</span>
  </td>;
}

/** One week of the plan calendar: its days, then a row with its unit bar, or the break's name when nothing is taught. */
function CalendarWeek({ week, selectedUnitId, onSelect }: { week: MonthWeek; selectedUnitId: string | null; onSelect(unit: MonthUnit): void }) {
  const breakName = week.days.find(d => d.kind === 'break')?.breakName;
  const span = week.barEnd - week.barStart + 1;
  return <tbody className="cal-week">
    <tr>{week.days.map(d => <DayCell key={d.date} d={d} />)}</tr>
    <tr className="cal-bars">
      {week.barStart > 0 && <td colSpan={week.barStart} />}
      <td colSpan={span}>
        {week.units.map(u => {
          const selected = u.id === selectedUnitId;
          return <button key={u.id} type="button"
            className={`cal-bar${u.done ? ' done' : ''}${u.pastLastDay ? ' over' : ''}${selected ? ' selected' : ''}`}
            style={{ '--unit-color': unitColor(u.index) } as CSSProperties}
            aria-pressed={selected}
            aria-label={`${u.name}, week of ${day(week.monday)}${u.done ? ', done' : ''}${u.pastLastDay ? ', past the last day' : ''}`}
            onClick={() => onSelect(u)}>
            <span className="cal-bar-name">{u.name}</span>
            {u.pastLastDay && <span className="cal-over" aria-hidden="true">over</span>}
          </button>;
        })}
        {week.units.length === 0 && breakName && <span className="cal-bar-label">{breakName}</span>}
      </td>
      {week.barEnd < 4 && <td colSpan={4 - week.barEnd} />}
    </tr>
  </tbody>;
}

/**
 * The unit picked in the calendar, and a container whose `[data-unit-id]` element for it is
 * scrolled into view on every pick, including a second pick of the same unit.
 */
export function useSelectedUnit() {
  const [pick, setPick] = useState<{ id: string; n: number } | null>(null);
  const container = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!pick) return;
    const target = Array.from(container.current?.querySelectorAll<HTMLElement>('[data-unit-id]') ?? []).find(el => el.dataset.unitId === pick.id);
    const still = window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;
    target?.scrollIntoView?.({ block: 'nearest', behavior: still ? 'auto' : 'smooth' });
  }, [pick]);
  return { selectedUnitId: pick?.id ?? null, select: (id: string) => setPick(p => ({ id, n: (p?.n ?? 0) + 1 })), container };
}
